/**
 * Typed facade for window-level global state.
 *
 * All reads/writes of __ORG_SETTINGS__, __ORG_TIME_FORMAT__, __FEATURE_FLAGS__,
 * and __BUTTON_STYLE__ go through this module so the `(window as any)` cast
 * lives in exactly one place.
 */

export type ButtonStyle = 'modern' | 'classic' | 'original';

export interface OrgSettings {
  name?: string;
  businessName?: string;
  defaultCurrency?: string;
  defaultCountryCode?: string;
  defaultPaymentMethod?: string;
  defaultScooterDailyPrice?: number;
  defaultScooterWeeklyPrice?: number;
  defaultScooterMonthlyPrice?: number;
  defaultContractDeposit?: number;
  defaultContractRentLengthDays?: number;
  timeFormat?: string;
  buttonStyle?: ButtonStyle;
  contractViewMode?: string;
  featureFlags?: Record<string, boolean>;
  [key: string]: unknown;
}

type FeatureFlags = Record<string, boolean>;

const w = window as unknown as Record<string, unknown>;

// ── OrgSettings ──────────────────────────────────────────────

export function getOrgSettings(): OrgSettings {
  return (w.__ORG_SETTINGS__ as OrgSettings) || {};
}

export function setOrgSettings(settings: OrgSettings): void {
  w.__ORG_SETTINGS__ = settings;
}

// ── Time format ──────────────────────────────────────────────

export function getOrgTimeFormat(): string {
  return (w.__ORG_TIME_FORMAT__ as string) || '';
}

export function setOrgTimeFormat(fmt: string | undefined): void {
  w.__ORG_TIME_FORMAT__ = fmt || '';
}

// ── Feature flags ────────────────────────────────────────────

export function getFeatureFlags(): FeatureFlags {
  return (w.__FEATURE_FLAGS__ as FeatureFlags) || {};
}

export function setFeatureFlags(flags: FeatureFlags): void {
  w.__FEATURE_FLAGS__ = flags;
}

// ── Button style ─────────────────────────────────────────────

export function getButtonStyle(): ButtonStyle {
  return (w.__BUTTON_STYLE__ as ButtonStyle) || 'original';
}

export function setButtonStyle(style: ButtonStyle): void {
  w.__BUTTON_STYLE__ = style;
}

/**
 * Batch-update all globals from a settings response.
 * Called after fetching / updating org settings.
 */
export function syncGlobalsFromSettings(settings: OrgSettings): void {
  setOrgSettings(settings);
  setOrgTimeFormat(settings.timeFormat);
  setFeatureFlags((settings.featureFlags as FeatureFlags) || {});
}
